// Live StreamNook cosmetics for the hosted overlay.
//
// A member's look (their member number, worn badge, Atmosphere, worn chat
// badges) lives in StreamNook's database. The desktop app repaints chat the
// moment one of those rows changes by following Supabase realtime on them; the
// overlay follows the same tables with the same public key, so a change reaches
// OBS in about a second instead of waiting for the chatter's next message.
//
// One realtime channel for the whole page, opened by the first feed that
// listens and closed by the last. Realtime does not replay what was missed
// while the socket was down, so a rejoin tells listeners to ask everything
// again.

import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

/** What changed. `memberId` is a StreamNook member's Twitch id; `everyMember`
 *  means a catalog row (an Atmosphere or a badge's art) or a row whose owner
 *  is not known changed, or realtime came back after a gap. */
export type MemberCosmeticsChange = { memberId: string } | { everyMember: true };

export type MemberCosmeticsListener = (change: MemberCosmeticsChange) => void;

// Each per-member table, and the column holding the member's Twitch id.
const MEMBER_TABLES: Record<string, string> = {
  user_profile_prefs: 'twitch_user_id', // Atmosphere and whether it shows in chat
  user_cosmetic_active: 'twitch_user_id', // the worn StreamNook badge
  user_cosmetic_equipment: 'twitch_user_id', // the typed loadout behind both
  user_identity: 'twitch_user_id', // worn chat badges (FFZ, BTTV Pro, ...)
  user_cosmetics: 'twitch_user_id', // what they own: gates badge and Atmosphere
  user_numbers: 'id', // a new member
};
// Catalog tables: a change can alter any member's look.
const CATALOG_TABLES = ['atmospheres', 'cosmetics'];

const listeners = new Set<MemberCosmeticsListener>();
let channel: RealtimeChannel | null = null;

function emit(change: MemberCosmeticsChange) {
  for (const listener of listeners) listener(change);
}

function open() {
  if (!supabase || channel) return;
  let joined = false;
  let ch = supabase.channel(`overlay-cosmetics-${Math.random().toString(36).slice(2, 10)}`);
  for (const [table, column] of Object.entries(MEMBER_TABLES)) {
    ch = ch.on('postgres_changes', { event: '*', schema: 'public', table }, (payload) => {
      const row = (payload.new && Object.keys(payload.new).length > 0 ? payload.new : payload.old) as Record<string, unknown>;
      const raw = row?.[column];
      const memberId = typeof raw === 'number' ? String(raw) : raw;
      // A delete carries only the primary key unless the table keeps whole old
      // rows, so an unknown owner refreshes every member on the page.
      emit(typeof memberId === 'string' && memberId ? { memberId } : { everyMember: true });
    });
  }
  for (const table of CATALOG_TABLES) {
    ch = ch.on('postgres_changes', { event: '*', schema: 'public', table }, () => emit({ everyMember: true }));
  }
  ch.subscribe((status) => {
    if (status !== 'SUBSCRIBED') return;
    if (joined) emit({ everyMember: true });
    joined = true;
  });
  channel = ch;
}

/** Follow StreamNook cosmetic changes. Returns a disposer. Without realtime
 *  (no public key in this build) nothing is heard and the feed's re-ask on new
 *  messages still applies. */
export function watchMemberCosmetics(listener: MemberCosmeticsListener): () => void {
  listeners.add(listener);
  open();
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    const ch = channel;
    channel = null;
    // Removing the last channel also closes the socket.
    if (ch && supabase) void supabase.removeChannel(ch).catch(() => {});
  };
}
